import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Heart, ShoppingCart, User, Menu, X, LayoutDashboard, LogOut, Truck, Zap, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";

const NAV = [
  { label: "Início", to: "/" },
  { label: "Produtos", to: "/loja" },
  { label: "Granel", to: "/loja?cat=granel" },
  { label: "Suplementos", to: "/loja?cat=suplementos" },
  { label: "Sem Glúten", to: "/loja?cat=granel" },
  { label: "Sem Lactose", to: "/loja?cat=alimentos" },
  { label: "Kits", to: "/loja?cat=kits" },
  { label: "Promoções", to: "/promocoes" },
];

const TOPBAR = [
  { icon: Truck, text: "Frete grátis em compras acima de R$199" },
  { icon: Zap, text: "Entrega rápida para você" },
  { icon: Lock, text: "Pagamento seguro" },
];

export function Header() {
  const { totals, setOpen } = useCart();
  const { user, logout } = useAuth();
  const { count: favCount } = useFavorites();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [q, setQ] = useState("");
  const [menu, setMenu] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/loja${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Utility bar */}
      <div className="bg-forest-deep text-alabaster">
        <div className="mx-auto flex max-w-[1280px] items-center justify-center gap-8 px-4 py-2 text-xs sm:px-6 md:justify-between">
          {TOPBAR.map((t, i) => (
            <span key={i} className={`flex items-center gap-1.5 font-sans ${i > 0 ? "hidden md:flex" : ""}`}>
              <t.icon size={13} className="text-ochre" /> {t.text}
            </span>
          ))}
        </div>
      </div>

      {/* Main bar */}
      <div className="border-b border-borderlight bg-alabaster">
        <div className="mx-auto flex max-w-[1280px] items-center gap-4 px-4 py-4 sm:px-6">
          <button onClick={() => setMenu(!menu)} data-testid="mobile-menu-toggle" className="text-forest-deep lg:hidden">{menu ? <X size={24} /> : <Menu size={24} />}</button>

          <Link to="/" data-testid="logo-link" className="flex flex-shrink-0 items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-clay/15 text-lg">🌱</span>
            <span className="flex flex-col leading-none">
              <span className="font-serif text-2xl font-semibold text-forest-deep">Multinativa</span>
              <span className="font-sans text-[9px] uppercase tracking-[0.25em] text-clay">Saúde Natural</span>
            </span>
          </Link>

          <form onSubmit={submitSearch} className="mx-auto hidden w-full max-w-xl items-center rounded-full border border-borderlight bg-white px-4 md:flex">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="O que você procura?" data-testid="header-search"
              className="w-full bg-transparent py-2.5 font-sans text-sm outline-none" />
            <button type="submit" data-testid="header-search-btn" className="flex h-8 w-8 items-center justify-center rounded-full bg-clay text-white"><Search size={16} /></button>
          </form>

          <div className="ml-auto flex items-center gap-5">
            {user?.role === "admin" && (
              <button onClick={() => navigate("/admin")} data-testid="admin-link" title="Painel" className="hidden text-forest-deep hover:text-clay sm:block"><LayoutDashboard size={20} /></button>
            )}
            {user ? (
              <div className="hidden items-center gap-2 sm:flex">
                <button onClick={() => navigate("/conta")} data-testid="account-link" className="flex items-center gap-2 text-forest-deep hover:text-clay">
                  <User size={20} /><span className="font-sans text-sm">{user.name.split(" ")[0]}</span>
                </button>
                <button onClick={logout} data-testid="logout-btn" title="Sair" className="text-forest-deep hover:text-clay"><LogOut size={18} /></button>
              </div>
            ) : (
              <button onClick={() => navigate("/login")} data-testid="login-link" className="hidden items-center gap-2 text-forest-deep hover:text-clay sm:flex">
                <User size={20} /><span className="font-sans text-sm">Minha conta</span>
              </button>
            )}
            <button onClick={() => navigate("/favoritos")} data-testid="favorites-link" title="Favoritos" className="relative text-forest-deep hover:text-clay">
              <Heart size={22} />
              {favCount > 0 && (
                <span data-testid="favorites-count" className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-clay px-1 font-sans text-[10px] font-semibold text-white">{favCount}</span>
              )}
            </button>
            <button onClick={() => setOpen(true)} data-testid="cart-toggle" className="relative text-forest-deep hover:text-clay">
              <ShoppingCart size={22} />
              {totals.count > 0 && (
                <span data-testid="cart-count" className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-clay px-1 font-sans text-[10px] font-semibold text-white">{totals.count}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Category nav */}
      <nav className="hidden border-b border-borderlight bg-alabaster lg:block">
        <div className="mx-auto flex max-w-[1280px] items-center gap-1 px-4 sm:px-6">
          {NAV.map((c) => {
            const current = pathname + (search || "");
            const active = c.to === "/" ? pathname === "/" : c.to === current;
            return (
              <Link key={c.label} to={c.to} data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`border-b-2 px-3 py-3 font-sans text-xs font-semibold uppercase tracking-[0.08em] transition-colors ${active ? "border-clay text-clay" : "border-transparent text-ink/70 hover:text-clay"}`}>{c.label}</Link>
            );
          })}
        </div>
      </nav>

      {/* Mobile menu */}
      {menu && (
        <div className="border-b border-borderlight bg-white px-4 py-3 lg:hidden" data-testid="mobile-menu">
          <form onSubmit={submitSearch} className="mb-3 flex items-center rounded-full border border-borderlight bg-white px-4">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="O que você procura?"
              className="w-full bg-transparent py-2.5 font-sans text-sm outline-none" />
            <Search size={18} className="text-ink/50" />
          </form>
          {NAV.map((c) => (
            <Link key={c.label} to={c.to} onClick={() => setMenu(false)} className="block py-2.5 font-sans text-sm uppercase tracking-wide text-ink">{c.label}</Link>
          ))}
          {user ? (
            <button onClick={() => { logout(); setMenu(false); }} className="block py-2.5 font-sans text-sm uppercase tracking-wide text-clay">Sair</button>
          ) : (
            <Link to="/login" onClick={() => setMenu(false)} className="block py-2.5 font-sans text-sm uppercase tracking-wide text-clay">Minha conta</Link>
          )}
        </div>
      )}
    </header>
  );
}
